import { useMemo } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { Input } from '@/shared/widgets/input';
import { Button } from '@/shared/widgets/button';
import { DropdownTray } from '@/shared/widgets/dropdown-tray';
import { useDispatch } from 'react-redux';
import { searchBuild } from '../../config/search.build';
import { useSearch } from '../hooks/useSearch';
import type { AppDispatch } from '@/app/store/store';
import { SEARCH_PLACEHOLDER } from '../../constants';
import SearchResults from './SearchResults';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

interface SearchPillTheme {
  bgColor: string;
  borderColor: string;
  textColor: string;
  placeholderColor: string;
  iconColor: string;
}

interface SearchProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
  /** Rounded full-width field + button (desktop header). */
  visualVariant?: 'default' | 'pill';
  pillTheme?: SearchPillTheme;
}

export default function Search({ 
  placeholder = SEARCH_PLACEHOLDER,
  onSearch,
  className = "",
  visualVariant = 'default',
  pillTheme,
}: SearchProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => searchBuild(dispatch), [dispatch]);
  const { analytics } = useAnalytics();
  const {
    searchQuery,
    setSearchQuery,
    isFocused,
    setIsFocused,
    searchResults,
    isLoading,
    handleSubmit,
    handleSuggestionClick,
    searchRef,
    listId,
  } = useSearch({
    service,
    onSearch,
  });

  const handleTrendingItemClick = (_url: string) => {
    // Let AppLink handle navigation (SPA); just close the dropdown / update UI here.
    setIsFocused(false);
  };

  const handleProductItemClick = (_url: string) => {
    // Let AppLink handle navigation (SPA); just close the dropdown / update UI here.
    setIsFocused(false);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    analytics?.trackEvent('search_icon_click', {
      location: 'header_desktop',
      action: 'submit_search',
      query: searchQuery || ''
    });
    analytics?.trackEvent('Product Searched', { 'Search Keyword': searchQuery || '' });
    handleSubmit(e);
  };

  const handleImageSearchClick = () => {
    analytics?.trackEvent('Image Search', { action: 'Clicked Image Search' });
  };

  const isPill = visualVariant === 'pill';

  return (
    <div
      ref={searchRef}
      className={`relative z-[41] flex-1 ${isPill ? 'max-w-2xl' : 'max-w-md'} ${className}`}
    >
      <form
        className={`flex items-center gap-0 ${isPill ? 'overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-sm' : ''}`}
        style={
          isPill && pillTheme
            ? { backgroundColor: pillTheme.bgColor, borderColor: pillTheme.borderColor }
            : undefined
        }
        onSubmit={handleSearchSubmit}
      >
        {isPill ? (
          <button
            type="submit"
            className="flex shrink-0 items-center pl-4 pr-1 text-left"
            aria-label="Submit search"
          >
            <SearchIcon className="h-[18px] w-[18px]" style={pillTheme ? { color: pillTheme.iconColor } : undefined} />
          </button>
        ) : null}
        <Input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          className={
            isPill
              ? 'h-11 flex-1 rounded-none border-0 bg-transparent pl-2 pr-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[var(--desktop-search-placeholder-color)]'
              : 'flex-1 h-10 rounded-l-md rounded-r-none border-gray-300 focus-visible:ring-0 hover:border-gray-500'
          }
          style={
            isPill && pillTheme
              ? ({
                  color: pillTheme.textColor,
                  '--desktop-search-placeholder-color': pillTheme.placeholderColor,
                } as React.CSSProperties)
              : undefined
          }
        />
        {!isPill ? (
          <Button
            type="submit"
            className="h-10 rounded-l-none rounded-r-md bg-hover px-5 text-white hover:bg-hover/90"
          >
            <SearchIcon className="h-5 w-5" />
          </Button>
        ) : null}
      </form>
      
      {/* Search Results Dropdown */}
      <DropdownTray
        isOpen={isFocused}
        className="left-0 w-[max(100%,420px)] max-w-[min(42rem,calc(100vw-2rem))]"
        maxHeight="600px"
      >
        <div className="p-4">
          <SearchResults
            searchResults={searchResults}
            searchQuery={searchQuery}
            isLoading={isLoading}
            onSuggestionClick={handleSuggestionClick}
            onTrendingClick={handleTrendingItemClick}
            onProductClick={handleProductItemClick}
            onImageSearchClick={handleImageSearchClick}
            onImageSearchSuccess={() => setIsFocused(false)}
            listId={listId}
          />
        </div>
      </DropdownTray>
    </div>
  );
}
